import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface GlobalOccasion {
  id: string;
  name: string;
  slug: string;
  occasion_date: string;
  relationship_filter: string[];
}

interface RelevantContact {
  id: string;
  full_name: string;
  relationship: string;
}

const OCCASION_EMOJI: Record<string, string> = {
  valentines_day: "💛",
  mothers_day: "💛",
  fathers_day: "💛",
  christmas: "🎄",
  new_years_day: "🎉",
};

const OCCASION_GREETING: Record<string, string> = {
  christmas: "Merry Christmas",
  new_years_day: "Happy New Year",
};

export default function GlobalOccasionCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cardData, setCardData] = useState<{
    occasion: GlobalOccasion;
    contacts: RelevantContact[];
  } | null>(null);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      // Check profile toggle
      const { data: profile } = await supabase
        .from("profiles")
        .select("global_occasion_reminders")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!profile?.global_occasion_reminders) return;

      const today = new Date();
      const todayStr = today.toISOString().slice(0, 10);

      // Fetch active global occasions
      const { data: occasions } = await supabase
        .from("global_occasions")
        .select("*")
        .eq("active", true);

      if (!occasions) return;

      // Find occasion matching today
      const todayOccasion = occasions.find((o: any) => {
        const occDate = new Date(o.occasion_date);
        return (
          occDate.getMonth() === today.getMonth() &&
          occDate.getDate() === today.getDate()
        );
      }) as GlobalOccasion | undefined;

      if (!todayOccasion) return;

      // Check dismissed
      const dismissKey = `circle_occasion_dismissed_${todayOccasion.slug}_${todayStr}`;
      if (localStorage.getItem(dismissKey) === "true") return;

      // Fetch contacts with relationship filter
      let query = supabase
        .from("contacts")
        .select("id, full_name, relationship")
        .eq("user_id", user.id);

      if (todayOccasion.relationship_filter && todayOccasion.relationship_filter.length > 0) {
        query = query.in("relationship", todayOccasion.relationship_filter);
      }

      const { data: contacts } = await query;
      if (!contacts || contacts.length === 0) return;

      let filtered = contacts as RelevantContact[];

      // Bereavement check for Mother's/Father's Day
      if (todayOccasion.slug === "mothers_day" || todayOccasion.slug === "fathers_day") {
        const { data: bereavements } = await supabase
          .from("occasions")
          .select("contact_id")
          .eq("user_id", user.id)
          .eq("occasion_type", "bereavement");

        if (bereavements && bereavements.length > 0) {
          const bereavementIds = new Set(bereavements.map((b: any) => b.contact_id));
          filtered = filtered.filter((c) => !bereavementIds.has(c.id));
        }
      }

      // Valentine's overlap check
      if (todayOccasion.slug === "valentines_day") {
        const { data: feb14 } = await supabase
          .from("occasions")
          .select("contact_id")
          .eq("user_id", user.id);

        if (feb14) {
          const overlapIds = new Set(
            feb14
              .filter((o: any) => {
                const d = new Date(o.date);
                return d.getMonth() === 1 && d.getDate() === 14;
              })
              .map((o: any) => o.contact_id)
          );
          filtered = filtered.filter((c) => !overlapIds.has(c.id));
        }
      }

      if (filtered.length === 0) return;

      setCardData({ occasion: todayOccasion, contacts: filtered });
    };

    load();
  }, [user]);

  if (!cardData) return null;

  const { occasion, contacts } = cardData;
  const emoji = OCCASION_EMOJI[occasion.slug] || "💛";
  const isGeneral = occasion.relationship_filter.length === 0;
  const greeting = OCCASION_GREETING[occasion.slug];
  const todayStr = new Date().toISOString().slice(0, 10);

  const handleDismiss = () => {
    localStorage.setItem(
      `circle_occasion_dismissed_${occasion.slug}_${todayStr}`,
      "true"
    );
    setCardData(null);
  };

  return (
    <div className="relative mb-4 rounded-2xl border-l-[3px] border-l-accent bg-card p-4 shadow-sm">
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      {isGeneral ? (
        <>
          <p className="text-sm font-semibold text-foreground mb-1">
            {emoji}&nbsp;&nbsp;{greeting || occasion.name}
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            Your Circle would love to hear from you today.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm font-medium text-accent hover:underline"
          >
            Open Circle →
          </button>
        </>
      ) : contacts.length === 1 ? (
        <>
          <p className="text-sm font-semibold text-foreground mb-1">
            {emoji}&nbsp;&nbsp;{occasion.name}
          </p>
          <p className="text-sm text-muted-foreground mb-1">
            Today is the day to reach out to {contacts[0].full_name}.
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            A message from you would mean more than you know.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm font-medium text-accent hover:underline"
          >
            Write {contacts[0].full_name} a Message →
          </button>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold text-foreground mb-1">
            {emoji}&nbsp;&nbsp;{occasion.name}
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            Today is the day to reach out to {contacts[0].full_name}
            {" "}and {contacts[1].full_name}
            {contacts.length > 2 ? ` and ${contacts.length - 2} more` : ""}.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm font-medium text-accent hover:underline"
          >
            Open Circle →
          </button>
        </>
      )}
    </div>
  );
}
